/**
 * @fileoverview Settings → Notifications: the three email toggles.
 *
 * `student_practice_logged` and `student_portfolio_generated` are deliberately
 * not listed: they deliver by push only, which never reaches a web-only
 * instructor, so showing them here promises a notification that won't arrive.
 * The prefs still exist and the mobile app still honors them — restore these
 * rows once they have an email channel too.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { SettingsRow, SettingsSection, Toggle } from "@/components/settings/SettingsSection";
import api from "@/lib/api";

const ROWS = [
  { key: "student_enrolled", label: "Student enrolled", desc: "Email you when a student claims your invite link" },
  { key: "lesson_summary_ready", label: "Lesson summary ready", desc: "Email you when your lesson summary is ready to review" },
  {
    key: "lesson_summary_copy",
    label: "Copy me on lesson summaries",
    desc: "BCC you on every summary you send, so you keep the exact copy the family received",
  },
];

const DEFAULTS = {
  student_enrolled: true,
  lesson_summary_ready: true,
  student_practice_logged: true,
  student_portfolio_generated: true,
  // Opt-in: an extra message in the instructor's inbox on every send, so it
  // has to be asked for rather than assumed.
  lesson_summary_copy: false,
};

export default function NotificationsSection() {
  const [prefs, setPrefs] = useState(DEFAULTS);

  useEffect(() => {
    const controller = new AbortController();
    api
      .get("/auth/me", { signal: controller.signal })
      .then((res) => {
        // Merged, not replaced: a stored map only holds keys the instructor
        // has actually toggled, so replacing it would flip every never-touched
        // preference to undefined (i.e. render as off) and lose its default.
        if (res.data?.notification_preferences) {
          setPrefs((prev) => ({ ...prev, ...res.data.notification_preferences }));
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) console.error(err);
      });
    return () => controller.abort();
  }, []);

  const toggle = async (key) => {
    const prev = prefs;
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try {
      await api.put("/auth/notification-preferences", updated);
    } catch (_e) {
      // Roll back so the toggle reflects what's actually saved.
      setPrefs(prev);
      toast.error("Couldn't save notification preference");
    }
  };

  return (
    <SettingsSection title="Notifications" testId="notifications-section">
      {ROWS.map((row, i) => (
        <SettingsRow
          key={row.key}
          title={row.label}
          desc={row.desc}
          trailing={<Toggle on={!!prefs[row.key]} onChange={() => toggle(row.key)} label={row.label} testId={`notif-${row.key}`} />}
          last={i === ROWS.length - 1}
        />
      ))}
    </SettingsSection>
  );
}
